import {
  AloftTime,
  ReconciliationCommentary,
  RevisionData,
  formatLongRevisionType,
  orUndefined,
} from '@aloft/toolkit-shared';
import { Flex, Icon, Typography } from '@aloft/toolkit-sui';

import { PropsWithChildren } from 'react';
import { Controller } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { ValidationError } from '../../../hooks/validation/types.ts';
import { ReconciliationFormReturn } from '../types.ts';

const RevisionTimelineRow = ({
  requestedAt,
  isLast,
  children,
}: PropsWithChildren<{
  requestedAt?: AloftTime;
  isLast?: boolean;
}>) => {
  const userTz = AloftTime.getCurrentTimeZone();
  const parsedRequestedAt = orUndefined(AloftTime.schema.parse)(requestedAt);
  const dateLine = parsedRequestedAt?.formatTime(userTz, 'MM/dd/yy');
  const timeLine = parsedRequestedAt?.formatTime(userTz, 'hh:mm aa zzz');

  return (
    <Flex gap="16px" alignItems="flex-start" paddingX="16px" paddingY="0">
      <Flex
        flexDirection="column"
        gap={1}
        alignItems="flex-start"
        justifyContent="flex-start"
        paddingTop="8px"
        width="72px"
        flexShrink={0}
        __dangerDoNotUseSx={{
          textTransform: 'uppercase',
        }}
      >
        {dateLine && (
          <Typography
            variant="subtitle2"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontSize: '12px', lineHeight: 1 }}
          >
            {dateLine}
          </Typography>
        )}
        {timeLine && (
          <Typography
            variant="subtitle2"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontSize: '10px', lineHeight: 1 }}
          >
            {timeLine}
          </Typography>
        )}
      </Flex>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        paddingTop="8px"
        flexShrink={0}
      >
        <Icon icon="edit-box" color="alertWarning" size="md" />
        {!isLast && (
          <Flex
            flex="1 0 0"
            width="1px"
            minHeight="8px"
            backgroundColor="alertInfoLight"
            borderRadius="4px"
            marginTop="4px"
          />
        )}
      </Flex>
      <Flex
        flex={1}
        flexDirection="column"
        gap={2}
        paddingTop="8px"
        paddingBottom="16px"
        minWidth={0}
      >
        {children}
      </Flex>
    </Flex>
  );
};

export const ReconciliationRevisionSection = ({
  active,
  form,
  isMockOrder,
  reconciliationCommentary,
  revisionData,
  previousRevisionsData,
  revisionCommentaryError,
  alternativeComparablesCommentaryError,
  setSectionRef,
}: {
  active: boolean;
  form: ReconciliationFormReturn;
  isMockOrder: boolean;
  reconciliationCommentary?: ReconciliationCommentary;
  revisionData?: RevisionData;
  previousRevisionsData?: RevisionData[];
  revisionCommentaryError?: ValidationError;
  alternativeComparablesCommentaryError?: ValidationError;
  setSectionRef: (el: HTMLDivElement | null) => void;
}) => {
  const revisionCommentaryLabel = `${formatLongRevisionType(
    revisionData?.revisionType || 'rov',
  )} Commentary`;

  return (
    <>
      <Typography variant="h1">Revision / ROV</Typography>
      <Flex
        ref={setSectionRef}
        backgroundColor={active ? 'surface3' : 'surface1'}
        borderRadius="5px"
        padding="12px"
        flexDirection="column"
        gap={0}
      >
        <Flex
          alignItems="center"
          gap="5px"
          marginBottom="12px"
          padding="8px"
          borderRadius="5px"
          borderColor="strokeDefault"
          borderStyle="solid"
          borderWidth="1px"
          backgroundColor="surface6"
        >
          <Typography variant="h4" color="textSecondary">
            Request Description:
          </Typography>
          <Typography
            variant="placeholder"
            color="textSecondary"
            __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
          >
            {revisionData?.requestDescription || ''}
          </Typography>
        </Flex>

        <RevisionTimelineRow
          requestedAt={revisionData?.requestedAt}
          isLast={
            !reconciliationCommentary?.previousRevisionCommentaries?.some(
              (c) => c.trim().length > 0,
            )
          }
        >
          <Flex flexDirection="column" gap={2}>
            <Controller
              name="revisionCommentary"
              control={form.control}
              render={({ fieldState }) => (
                <FormTextarea
                  name="revisionCommentary"
                  requiredFieldName="revisionCommentary"
                  control={form.control}
                  label={revisionCommentaryLabel}
                  placeholder={isMockOrder ? 'Optional' : 'Required'}
                  isRequired={!isMockOrder}
                  labelWidth="250px"
                  inputWidth="500px"
                  height="200px"
                  labelColor="textSecondary"
                  labelVariant="h3"
                  size="sm"
                  resize="vertical"
                  customErrorMessage={
                    fieldState.isTouched ? revisionCommentaryError?.message : undefined
                  }
                  column
                />
              )}
            />
            {revisionData?.comparables?.map((comp, index) => (
              <Flex flex={1} flexDirection="column" key={comp.address} gap={2}>
                <Typography variant="h3" color="textSecondary">
                  <>
                    {`Alternative Comparable ${index + 1}: ${comp.address}`}
                    {!isMockOrder ? <span style={{ color: 'red' }}> *</span> : null}
                  </>
                </Typography>
                <Controller
                  name={`alternativeComparablesCommentary.${index}` as const}
                  control={form.control}
                  render={({ fieldState }) => (
                    <FormTextarea
                      name={`alternativeComparablesCommentary.${index}` as const}
                      requiredFieldName="alternativeComparablesCommentary"
                      control={form.control}
                      placeholder={isMockOrder ? 'Optional' : 'Required'}
                      isRequired={!isMockOrder}
                      labelWidth="250px"
                      inputWidth="500px"
                      labelColor="textSecondary"
                      labelVariant="h3"
                      size="sm"
                      resize="vertical"
                      column
                      customErrorMessage={
                        fieldState.isTouched &&
                        reconciliationCommentary?.alternativeComparablesCommentary?.[index] === ''
                          ? alternativeComparablesCommentaryError?.message
                          : undefined
                      }
                    />
                  )}
                />
              </Flex>
            ))}
          </Flex>
        </RevisionTimelineRow>

        {(reconciliationCommentary?.previousRevisionCommentaries ?? [])
          .map((commentary, index) => ({
            commentary,
            revisionType: previousRevisionsData?.[index]?.revisionType ?? 'revision',
            requestedAt: previousRevisionsData?.[index]?.requestedAt,
            altComps:
              reconciliationCommentary?.previousAlternativeComparablesCommentaries?.[index]
                ?.comparables ?? [],
          }))
          .filter(({ commentary }) => commentary.trim().length > 0)
          .toReversed()
          .map(({ commentary, revisionType: prevType, requestedAt, altComps }, i, arr) => (
            <RevisionTimelineRow key={i} requestedAt={requestedAt} isLast={i === arr.length - 1}>
              <Flex flexDirection="column" gap={2}>
                <Typography variant="h3" color="textSecondary">
                  {`${formatLongRevisionType(prevType)} Commentary`}
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                >
                  {commentary}
                </Typography>
                {altComps.map((comp, altIndex) => (
                  <Flex key={altIndex} gap="16px" paddingLeft="0" alignItems="flex-start">
                    <Flex
                      flexDirection="column"
                      alignItems="flex-start"
                      justifyContent="center"
                      paddingTop="4px"
                    >
                      <Typography
                        variant="subtitle2"
                        color="textTertiary"
                        __dangerDoNotUseSx={{
                          textTransform: 'uppercase',
                          width: '72px',
                        }}
                      >
                        {`Alt. Comp. ${altIndex + 1}`}
                      </Typography>
                    </Flex>
                    <Flex flexDirection="column" gap={1}>
                      <Typography variant="h4" color="textSecondary">
                        {comp.address}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {comp.commentary}
                      </Typography>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </RevisionTimelineRow>
          ))}
      </Flex>
    </>
  );
};
